import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { billingApi, type PlanStatus, type SubscriptionInvoice } from '../lib/api'
import { money } from '../lib/money'
import { useApp } from '../lib/store'
import { AppBar } from '../components/AppBar'

export function BillingScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const refreshCatalog = useApp((s) => s.refreshCatalog)

  const [status, setStatus] = useState<PlanStatus | null>(null)
  const [invoice, setInvoice] = useState<SubscriptionInvoice | null>(null)
  const [busy, setBusy] = useState(false)
  const poll = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    billingApi.status().then(setStatus).catch(() => setStatus(null))
    return () => {
      if (poll.current) clearInterval(poll.current)
    }
  }, [])

  /*
   * Tiền về là SePay gọi webhook tới máy chủ, trình duyệt không biết gì cả — nên phải
   * hỏi lại. Hỏi mỗi 4 giây trong 5 phút: quán chuyển khoản xong thường thấy báo trong
   * vòng chục giây, quá 5 phút thì để họ tự bấm kiểm tra chứ đừng hỏi mãi.
   */
  function watch(id: string) {
    if (poll.current) clearInterval(poll.current)

    const started = Date.now()
    poll.current = setInterval(async () => {
      if (Date.now() - started > 5 * 60_000) {
        if (poll.current) clearInterval(poll.current)
        return
      }
      try {
        const fresh = await billingApi.getInvoice(id)
        setInvoice(fresh)
        if (fresh.status === 'Paid') {
          if (poll.current) clearInterval(poll.current)
          setStatus(await billingApi.status())
          await refreshCatalog()
          say('Đã nhận tiền — gói đã được gia hạn')
        }
      } catch {
        /* mất mạng giữa chừng: lần hỏi sau tiếp tục */
      }
    }, 4000)
  }

  async function buy(months: number) {
    setBusy(true)
    try {
      const inv = await billingApi.createInvoice(months)
      setInvoice(inv)
      watch(inv.id)
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa tạo được hoá đơn')
    }
    setBusy(false)
  }

  // ── Đang chờ chuyển khoản ──
  if (invoice && invoice.status !== 'Paid') {
    return (
      <div className="screen">
        <AppBar
          title="Chuyển khoản"
          sub={`${invoice.months} tháng · ${money(invoice.amount)}đ`}
          onBack={() => {
            if (poll.current) clearInterval(poll.current)
            setInvoice(null)
          }}
        />

        <div className="body">
          {invoice.qrUrl && (
            <div className="qr-box">
              <img className="qr-canvas" src={invoice.qrUrl} alt="Mã QR chuyển khoản" width={240} />
            </div>
          )}

          <div className="card">
            <div className="pay-row">
              <span>Ngân hàng</span>
              <b>{invoice.bankCode}</b>
            </div>
            <div className="pay-row">
              <span>Số tài khoản</span>
              <b className="num">{invoice.accountNumber}</b>
            </div>
            {invoice.accountName && (
              <div className="pay-row">
                <span>Chủ tài khoản</span>
                <b>{invoice.accountName}</b>
              </div>
            )}
            <div className="pay-row big divider">
              <span>Số tiền</span>
              <b className="num">{money(invoice.amount)}</b>
            </div>
            <div className="pay-row">
              <span>Nội dung chuyển khoản</span>
              <b className="num">{invoice.code}</b>
            </div>
          </div>

          <p className="hint">
            Quét mã là điền sẵn hết. Nếu gõ tay thì <b>nội dung phải đúng {invoice.code}</b> — sai
            nội dung là hệ thống không biết tiền của quán nào.
          </p>

          <div className="waiting">
            <span className="sync-dot" aria-hidden="true" />
            Đang chờ tiền về…
          </div>
        </div>

        <div className="dock">
          <button
            type="button"
            className="btn btn-ghost grow"
            onClick={async () => {
              try {
                const fresh = await billingApi.getInvoice(invoice.id)
                setInvoice(fresh)
                if (fresh.status !== 'Paid') say('Chưa thấy tiền về. Chờ thêm chút nữa.')
              } catch {
                say('Chưa kiểm tra được')
              }
            }}
          >
            Đã chuyển, kiểm tra
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="screen">
      <AppBar title="Gói dịch vụ" onBack={() => nav('/settings')} />

      <div className="body">
        <div className={`card${status?.active ? '' : ' warn-card'}`}>
          <div className="pay-row big">
            <span>{status?.active ? 'Gói Quán nhỏ' : 'Gói miễn phí'}</span>
          </div>
          {status?.active ? (
            <p className="note">
              Còn <b>{status.daysLeft} ngày</b>
              {status.expiresAt
                ? ` · hết hạn ${new Date(status.expiresAt).toLocaleDateString('vi-VN')}`
                : ''}
              . Gia hạn sớm thì ngày còn lại được cộng dồn, không mất.
            </p>
          ) : (
            <p className="note">
              Gói miễn phí giới hạn 30 sản phẩm và 50 hoá đơn mỗi ngày, và{' '}
              <b>không có báo cáo thuế</b>.
            </p>
          )}
        </div>

        {status && !status.paymentReady && (
          <div className="card warn-card">
            <p>Kênh thanh toán chưa mở. Liên hệ sigoPOS để nâng gói.</p>
          </div>
        )}

        {status?.paymentReady && (
          <>
            <h2 className="section-label">Chọn gói</h2>
            <div className="list">
              {status.packages.map((p) => (
                <button
                  key={p.months}
                  type="button"
                  className="row-btn"
                  disabled={busy}
                  onClick={() => buy(p.months)}
                >
                  <span className="row-text">
                    <b>{p.label}</b>
                    <small className="num">
                      {money(Math.round(p.amount / p.months))}đ/tháng
                    </small>
                  </span>
                  <span className="row-price num">{money(p.amount)}</span>
                </button>
              ))}
            </div>
            <p className="note">
              Chuyển khoản qua mã QR, hệ thống tự nhận và gia hạn — thường trong vòng một phút.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
